import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Building2 } from "lucide-react";
import { Navbar, Footer } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function ComplaintRespond() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [complaint, setComplaint] = useState(null);
  const [error, setError] = useState("");
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError("Link de resposta inválido."); return; }
    api.get(`/complaints/respond/${token}`)
      .then((r) => setComplaint(r.data))
      .catch((err) => setError(formatApiError(err.response?.data?.detail)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/complaints/respond", { token, response });
      setDone(true);
      toast.success("Resposta publicada com sucesso!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafaf8]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14">
        {error ? (
          <div data-testid="respond-error" className="bg-white rounded-2xl border border-[#e7e5e4] p-10 text-center text-[#78716c]">{error}</div>
        ) : !complaint ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1b5e3f]" /></div>
        ) : done ? (
          <div data-testid="respond-success" className="bg-white rounded-2xl border border-[#e7e5e4] p-10 text-center">
            <CheckCircle2 className="w-12 h-12 text-[#1b5e3f] mx-auto mb-4" />
            <h1 className="font-head text-2xl font-bold text-[#1c1917]">Resposta publicada!</h1>
            <p className="text-[#78716c] mt-2">O consumidor foi notificado e a sua resposta já aparece publicamente na página Reclame Aqui.</p>
            <Button onClick={() => navigate("/reclame-aqui")} className="mt-6 rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]">Ver página Reclame Aqui</Button>
          </div>
        ) : (
          <>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#d97706]"><Building2 className="w-3.5 h-3.5" /> Resposta da empresa</span>
            <h1 className="font-head text-3xl font-bold text-[#1c1917] mt-2">Responder à reclamação</h1>
            <div className="bg-white rounded-2xl border border-[#e7e5e4] p-6 mt-6">
              <p className="text-xs text-[#a8a29e]">Reclamação de {complaint.user_name} · {new Date(complaint.created_at).toLocaleDateString("pt-PT")}</p>
              <p className="font-head font-semibold text-[#1c1917] mt-1">{complaint.title}</p>
              <p className="text-sm text-[#57534e] mt-2 whitespace-pre-wrap">{complaint.description}</p>
              {complaint.images?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {complaint.images.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer"><img src={url} alt="Prova" className="w-20 h-20 rounded-xl object-cover border border-[#e7e5e4]" /></a>
                  ))}
                </div>
              )}
            </div>
            {complaint.status === "resolvida" ? (
              <div className="bg-[#e8f0ec] border border-[#1b5e3f]/20 rounded-2xl p-6 mt-6 text-center text-[#1b5e3f] font-medium" data-testid="respond-already-resolved">
                Esta reclamação já foi marcada como resolvida pelo consumidor. ✅
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6">
                <label className="text-sm font-medium text-[#1c1917]">Resposta pública da empresa ({complaint.company_name})</label>
                <textarea data-testid="respond-textarea" required minLength={10} rows={6} value={response} onChange={(e) => setResponse(e.target.value)} placeholder="Escreva a resposta oficial da empresa ao consumidor (mín. 10 caracteres)" className="w-full bg-white border border-[#e7e5e4] rounded-xl px-4 py-3 mt-1.5 focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f] transition-all resize-y" />
                {complaint.response && <p className="text-xs text-[#78716c] mt-2">Já existe uma resposta publicada — enviar novamente irá substituí-la.</p>}
                <Button data-testid="respond-submit-btn" type="submit" disabled={busy} className="w-full mt-4 rounded-xl bg-[#1b5e3f] hover:bg-[#13422c] py-6 text-base">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Publicar resposta"}
                </Button>
              </form>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
